import { api, ApiError, apiErrorMessage, useAuthStore } from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    Banner,
    Button,
    ConfirmDialog,
    DateTime,
    KeyValueList,
    ObjectRef,
    PageHeader,
    PublicId,
    ReasonDialog,
    Section,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { ScreeningCaseDetail, ScreeningHit } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';

type Decision = 'clear' | 'confirm';

const ADJUSTMENT: Record<string, string> = {
    dob: 'Year of birth',
    country: 'Country',
    id: 'Id number',
    weak_alias: 'Weak a.k.a.',
};

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

/** One list entry the subject matched: the name score, each adjustment with its delta, the total (task F2). */
export function HitsTable({ hits }: { hits: readonly ScreeningHit[] }) {
    return (
        <div className="overflow-x-auto">
            <Table data-testid="screening-hits">
                <TableHeader>
                    <TableRow>
                        <TableHead>Entry</TableHead>
                        <TableHead>List</TableHead>
                        <TableHead>Programs</TableHead>
                        <TableHead>Name score</TableHead>
                        <TableHead>Adjustments</TableHead>
                        <TableHead>Total</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {hits.map((h) => (
                        <TableRow key={h.entry_id}>
                            <TableCell>
                                <div className="font-medium">
                                    {h.primary_name}
                                </div>
                                {h.matched_name !== h.primary_name ? (
                                    <div className="text-muted-foreground text-xs">
                                        matched a.k.a. {h.matched_name}
                                    </div>
                                ) : null}
                                <div className="text-muted-foreground font-mono text-xs">
                                    uid {h.entry_uid} · {h.type}
                                </div>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                                {h.list}
                            </TableCell>
                            <TableCell>
                                {h.programs.join(', ') || '—'}
                            </TableCell>
                            <TableCell>{h.name_score}</TableCell>
                            <TableCell>
                                {h.adjustments.length === 0 ? (
                                    '—'
                                ) : (
                                    <ul className="m-0 list-none space-y-1 p-0">
                                        {h.adjustments.map((a) => (
                                            <li key={a.kind}>
                                                <span className="font-mono">
                                                    {signed(a.delta)}
                                                </span>{' '}
                                                {ADJUSTMENT[a.kind] ?? a.kind}
                                                <span className="text-muted-foreground block text-xs">
                                                    {a.detail}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </TableCell>
                            <TableCell className="font-semibold">
                                {h.total}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

const COPY: Record<
    Decision,
    { title: string; description: string; confirm: string }
> = {
    clear: {
        title: 'Clear as a false match',
        description:
            'The subject is whitelisted against every entry above with your reason. A held buyer payment is captured; a blocked onboarding can be approved; a sanctions payout pause is lifted when no other sanctions case of the merchant is open.',
        confirm: 'Clear match',
    },
    confirm: {
        title: 'Confirm the sanctions match',
        description:
            'A held buyer payment is voided and the buyer flagged; an application in onboarding is rejected; a live merchant gets an account suspension proposed in Approvals. File the OFAC report by the case deadline.',
        confirm: 'Confirm match',
    },
};

/** BM-125 slice 4 (task F2): one sanctions or PEP case: the subject, the hits with their breakdown, the deadline, clear and confirm. */
export function ScreeningCasePage() {
    const { id = '' } = useParams();
    const can = useAuthStore((s) => s.can);
    const me = useMe();
    const path = `/staff/risk/screening/cases/${id}`;
    const query = useOne<ScreeningCaseDetail>(path);
    const [decision, setDecision] = useState<{
        kind: Decision;
        key: string;
    } | null>(null);
    const [conflict, setConflict] = useState(false);
    const decide = useWrite(
        async (body: {
            kind: Decision;
            key: string;
            reason: string;
            version: number;
        }) =>
            (
                await api().post(
                    `${path}/${body.kind}`,
                    { reason: body.reason, version: body.version },
                    { idempotencyKey: body.key },
                )
            ).data,
        [
            path,
            '/staff/risk/screening/cases',
            '/staff/risk/screening/whitelist',
            '/staff/risk/screening/subjects',
            '/staff/merchants',
        ],
    );
    const refused = (error: unknown) => {
        if (!ApiError.is(error)) return;
        if (error.status === 409) {
            setDecision(null);
            setConflict(true);
            void query.refetch();
            return;
        }
        setDecision((d) => (d ? { ...d, key: crypto.randomUUID() } : d));
        void query.refetch();
    };
    const decider = can(P.riskScreeningDecide);
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Screening', href: '/risk/screening' }]}
                {...detailTitle(
                    query,
                    query.data?.data.subject_label ?? null,
                    id,
                    'Sanctions case',
                )}
                meta={
                    <>
                        <PublicId
                            id={query.data?.data.id ?? id}
                            short={false}
                        />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
            />
            <DetailBody query={query} title="Sanctions case">
                {(c) => (
                    <div className="space-y-4">
                        {conflict ? (
                            <Banner
                                tone="warning"
                                title="This case changed since you opened it"
                            >
                                Someone decided it meanwhile; the page shows its
                                state now.
                            </Banner>
                        ) : null}
                        {c.approval_blocked ? (
                            <Banner
                                tone="warning"
                                title="The merchant's approval waits for this case"
                            >
                                The onboarding application cannot be approved
                                while this case is open.
                            </Banner>
                        ) : null}
                        {c.payouts_paused ? (
                            <Banner tone="danger" title="Payouts are paused">
                                A sanctions pause holds the merchant's payouts
                                until its sanctions cases are cleared.
                            </Banner>
                        ) : null}
                        <Section
                            title="Subject"
                            action={
                                decider && c.state === 'open' ? (
                                    <div
                                        className="flex flex-wrap gap-2"
                                        data-testid="screening-actions"
                                    >
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                decide.reset();
                                                setDecision({
                                                    kind: 'clear',
                                                    key: crypto.randomUUID(),
                                                });
                                            }}
                                        >
                                            Clear
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => {
                                                decide.reset();
                                                setDecision({
                                                    kind: 'confirm',
                                                    key: crypto.randomUUID(),
                                                });
                                            }}
                                        >
                                            Confirm match
                                        </Button>
                                    </div>
                                ) : undefined
                            }
                        >
                            <KeyValueList
                                items={[
                                    {
                                        label: 'Subject',
                                        value: (
                                            <span className="flex flex-wrap items-center gap-2">
                                                <StatusBadge
                                                    status={c.subject_kind}
                                                    domain={
                                                        ADMIN_STATUSES.screeningSubjectKind
                                                    }
                                                />
                                                {c.subject_label}
                                            </span>
                                        ),
                                    },
                                    ...(c.subjects ?? [])
                                        .filter(
                                            (s) =>
                                                s.subject_label !==
                                                c.subject_label,
                                        )
                                        .map((s) => ({
                                            label: 'Also',
                                            value: `${s.subject_label}${s.dob_year ? ` (${s.dob_year})` : ''}`,
                                        })),
                                    {
                                        label: 'Merchant',
                                        value: (
                                            <ObjectRef
                                                id={c.merchant_id}
                                                label={
                                                    c.merchant_name ?? undefined
                                                }
                                            />
                                        ),
                                    },
                                    ...(c.charge_id
                                        ? [
                                              {
                                                  label: 'Payment',
                                                  value: (
                                                      <ObjectRef
                                                          id={c.charge_id}
                                                      />
                                                  ),
                                              },
                                          ]
                                        : []),
                                    ...(c.verdict
                                        ? [
                                              {
                                                  label: 'At Pay',
                                                  value:
                                                      c.verdict === 'decline'
                                                          ? 'Declined'
                                                          : 'Held for review',
                                              },
                                          ]
                                        : []),
                                    {
                                        label: 'State',
                                        value: (
                                            <StatusBadge
                                                status={c.state}
                                                domain={
                                                    ADMIN_STATUSES.screeningCase
                                                }
                                            />
                                        ),
                                    },
                                    {
                                        label: 'Report due',
                                        value: (
                                            <span className="flex flex-wrap items-center gap-2">
                                                <DateTime
                                                    value={c.sla_due_at}
                                                />
                                                {c.late ? (
                                                    <Badge variant="danger">
                                                        Late
                                                    </Badge>
                                                ) : null}
                                            </span>
                                        ),
                                    },
                                    ...(c.decided_at
                                        ? [
                                              {
                                                  label: 'Decided',
                                                  value: (
                                                      <span>
                                                          <DateTime
                                                              value={
                                                                  c.decided_at
                                                              }
                                                          />{' '}
                                                          by{' '}
                                                          {who(
                                                              c.decided_by,
                                                              me,
                                                          )}
                                                          : {c.reason}
                                                      </span>
                                                  ),
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                            <p
                                className="text-muted-foreground mt-2 text-sm"
                                data-testid="report-note"
                            >
                                {c.report_note}
                            </p>
                        </Section>
                        <Section title={`List matches (${c.hits.length})`}>
                            <HitsTable hits={c.hits} />
                        </Section>
                        <Section title="Notes">
                            {c.notes.length === 0 ? (
                                <p className="text-muted-foreground text-sm">
                                    No notes yet.
                                </p>
                            ) : (
                                <ul className="m-0 list-none space-y-2 p-0">
                                    {c.notes.map((n) => (
                                        <li key={n.id} className="text-sm">
                                            <span className="text-muted-foreground text-xs">
                                                <DateTime
                                                    value={n.created_at}
                                                />{' '}
                                                · {who(n.author, me)}
                                            </span>
                                            <div>{n.body}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <p className="mt-2 text-sm">
                                <Link
                                    to="/risk/screening/whitelist"
                                    className="hover:underline"
                                >
                                    Whitelist
                                </Link>
                            </p>
                        </Section>

                        {decision?.kind === 'clear' ? (
                            <ReasonDialog
                                open
                                onOpenChange={(o) =>
                                    o ? null : setDecision(null)
                                }
                                title={COPY.clear.title}
                                description={COPY.clear.description}
                                confirmLabel={COPY.clear.confirm}
                                required
                                busy={decide.isPending}
                                error={decide.error}
                                onConfirm={(reason) =>
                                    decide.mutate(
                                        {
                                            ...decision,
                                            reason,
                                            version: c.version,
                                        },
                                        {
                                            onSuccess: () => setDecision(null),
                                            onError: refused,
                                        },
                                    )
                                }
                            />
                        ) : null}
                        {decision?.kind === 'confirm' ? (
                            <ConfirmDialog
                                open
                                onCancel={() => setDecision(null)}
                                title={COPY.confirm.title}
                                body={COPY.confirm.description}
                                confirmLabel={COPY.confirm.confirm}
                                destructive
                                loading={decide.isPending}
                                reason={{ required: true, enforce: true }}
                                error={
                                    decide.error
                                        ? apiErrorMessage(decide.error)
                                        : undefined
                                }
                                onConfirm={({ reason }) =>
                                    decide.mutate(
                                        {
                                            ...decision,
                                            reason,
                                            version: c.version,
                                        },
                                        {
                                            onSuccess: () => setDecision(null),
                                            onError: refused,
                                        },
                                    )
                                }
                            />
                        ) : null}
                    </div>
                )}
            </DetailBody>
        </div>
    );
}
